import {
  addWrapper,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
  addRef,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { useDropzone } from 'react-dropzone'
import { addApi } from 'shared/src/api'
import {
  addDataLoadingFromPromise,
  isError,
  isLoading,
} from 'shared/src/utils/dataLoading'
import {
  addPropTrackingRef,
  branchIfPassesPredicate,
  getContextHelpers,
  toObjectKeys,
} from 'ad-hok-utils'
import Hls from 'hls.js'
import {
  MediaContent,
  Image as ApiImage,
  Video as ApiVideo,
  isImageStateError,
  isImageStateProcessing,
  isVideoStateProcessing,
  isVideoStateError,
} from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'
import cx from 'classnames'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import mediaContentEmpty from '../assets/media-content-empty.svg'
import mediaContentLoadingBackground from '../assets/media-content-loading-background.svg'
import mediaContentLoadingSpinner from '../assets/media-content-loading-spinner.svg'
import productEmpty from '../assets/product-empty.svg'
import productLoadingBackground from '../assets/product-image-loading-background.svg'
import profileAvatarEmpty from '../assets/profile-avatar-empty.svg'
import profileAvatarLoadingBackground from '../assets/profile-avatar-loading-background.svg'
import profileAvatarMask from '../assets/profile-avatar-mask.svg'
import { IMAGE_CONTENT_TYPES, VIDEO_CONTENT_TYPES } from './ImageField'
import { addToastContext } from './toastContext'
import Icon from './Icon'

type Mode = 'media' | 'product' | 'profile'

interface ModeContext {
  mode: Mode
}

const [addModeContextProvider, addModeContext] = getContextHelpers<ModeContext>(
  toObjectKeys(['mode'])
)

const MODE_IMAGE_WIDTHS: Record<Mode, number> = {
  media: 70,
  product: 210,
  profile: 96,
}

const MODE_IMAGE_HEIGHTS: Record<Mode, number> = {
  media: 102,
  product: 204,
  profile: 92,
}

const MODE_BORDER_RADII: Record<Mode, number | undefined> = {
  media: 2,
  product: undefined,
  profile: undefined,
}

const MODE_BORDERS: Record<Mode, string | undefined> = {
  media: '1px solid rgba(144, 115, 79, 0.24)',
  product: undefined,
  profile: undefined,
}

const MODE_EMPTY_IMAGES: Record<Mode, string> = {
  media: mediaContentEmpty,
  product: productEmpty,
  profile: profileAvatarEmpty,
}

const MODE_LOADING_IMAGES: Record<Mode, string> = {
  media: mediaContentLoadingBackground,
  product: productLoadingBackground,
  profile: profileAvatarLoadingBackground,
}

const useStyles = createUseStyles({
  container: {
    width: ({ mode }: ModeContext) => MODE_IMAGE_WIDTHS[mode],
    height: ({ mode }: ModeContext) => MODE_IMAGE_HEIGHTS[mode],
    flexGrow: 0,
    flexShrink: 0,
    boxSizing: 'border-box',
    background: 'rgba(54, 52, 50, 0.02)',
    borderRadius: ({ mode }: ModeContext) => MODE_BORDER_RADII[mode],
    overflow: 'hidden',
    border: ({ mode }: ModeContext) => MODE_BORDERS[mode],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  image: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
  loadingContainer: {
    position: 'relative',
  },
  loadingSpinner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    animation: '$rotation 1s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  '@keyframes rotation': {
    '0%': {
      transform: 'translate(-50%, -50%) rotate(0deg)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) rotate(360deg)',
    },
  },
  profileAvatarMask: {
    maskImage: `url(${profileAvatarMask})`,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

const MODE_MASK_CLASSES: Record<
  Mode,
  keyof ReturnType<typeof useStyles> | undefined
> = {
  media: undefined,
  product: undefined,
  profile: 'profileAvatarMask',
}
const getModeMaskClassName = (
  classes: ReturnType<typeof useStyles>,
  mode: Mode
): string | undefined => {
  const classNameKey = MODE_MASK_CLASSES[mode]
  return classNameKey ? classes[classNameKey] : undefined
}

const Empty: FC = flowMax(
  addDisplayName('Empty'),
  addModeContext,
  addTranslationHelpers,
  addProps(({ mode }) => ({
    classes: useStyles({ mode }),
  })),
  ({ mode, classes, t }) => (
    <img
      src={MODE_EMPTY_IMAGES[mode]}
      alt={t('admin.resources.images.uploadImage')}
      className={getModeMaskClassName(classes, mode)}
    />
  )
)

export const Loading: FC = flowMax(
  addDisplayName('Loading'),
  addModeContext,
  addProps(({ mode }) => ({
    classes: useStyles({ mode }),
  })),
  addTranslationHelpers,
  ({ mode, classes, t }) => (
    <div className={classes.loadingContainer}>
      <img
        src={MODE_LOADING_IMAGES[mode]}
        alt={t('admin.resources.images.loading')}
        className={getModeMaskClassName(classes, mode)}
      />
      <img
        src={mediaContentLoadingSpinner}
        alt=""
        className={classes.loadingSpinner}
      />
    </div>
  )
)

type UploaderState = 'waiting' | 'uploading' | 'uploadError'

interface UploaderProps {
  onUploadedImage: (image: ApiImage) => void
  onUploadedVideo?: (video: ApiVideo) => void
  onDelete: () => void
  className?: string
}

const Uploader: FC<UploaderProps> = flowMax(
  addDisplayName('Uploader'),
  addApi,
  addState('state', 'setState', typedAs<UploaderState>('waiting')),
  addState(
    'abortController',
    'setAbortController',
    typedAs<AbortController | null>(null)
  ),
  addHandlers({
    uploadImage:
      ({ api, setState, onUploadedImage, setAbortController }) =>
      async (file: File) => {
        setState('uploading')

        const abortController = new AbortController()
        setAbortController(abortController)

        try {
          const { presignedUrl, key } = await api.images.presignedUrl({
            filename: file.name,
          })
          await fetch(decodeURIComponent(presignedUrl), {
            method: 'PUT',
            body: file,
            signal: abortController.signal,
          })
          const image = await api.images.create({
            type: 's3',
            value: key,
          })

          onUploadedImage(image)
          setState('waiting')
        } catch (error) {
          console.error('Error uploading file', error)

          if (error instanceof DOMException && error.name === 'AbortError') {
            setState('waiting')
          } else {
            setState('uploadError')
          }
        }
      },
    uploadVideo:
      ({ api, setState, onUploadedVideo, setAbortController }) =>
      async (file: File) => {
        setState('uploading')

        const abortController = new AbortController()
        setAbortController(abortController)

        try {
          const { presignedUrl, id } = await api.videos.presignedUrl()
          await fetch(decodeURIComponent(presignedUrl), {
            method: 'PUT',
            body: file,
            signal: abortController.signal,
          })

          const { playbackId } = await api.videos.playbackId(id)

          const video = await api.videos.create({
            type: 'mux',
            value: playbackId,
          })

          onUploadedVideo!(video)
          setState('waiting')
        } catch (error) {
          console.error('Error uploading file to Mux', error)

          if (error instanceof DOMException && error.name === 'AbortError') {
            setState('waiting')
          } else {
            setState('uploadError')
          }
        }
      },
  }),
  addHandlers({
    onDrop:
      ({ uploadImage, uploadVideo, onDelete }) =>
      async (files: File[]) => {
        if (files.length !== 1) {
          return
        }

        onDelete()

        const [file] = files

        if (VIDEO_CONTENT_TYPES.includes(file.type)) {
          await uploadVideo(file)
        } else {
          await uploadImage(file)
        }
      },
    onCancel:
      ({ abortController }) =>
      () => {
        abortController?.abort()
      },
  }),
  addProps(({ state }) => ({
    disabled: state === 'uploading',
  })),
  addModeContext,
  addTranslationHelpers,
  addProps(({ mode }) => ({
    classes: useStyles({ mode }),
  })),
  ({
    onDrop,
    state,
    disabled,
    onUploadedVideo,
    children,
    classes,
    className,
  }) => {
    const { getRootProps, getInputProps } = useDropzone({
      disabled,
      accept: (onUploadedVideo
        ? [...IMAGE_CONTENT_TYPES, ...VIDEO_CONTENT_TYPES]
        : IMAGE_CONTENT_TYPES
      ).join(','),
      onDrop,
    })

    return (
      <div>
        <div {...getRootProps()} className={cx(classes.container, className)}>
          <input {...getInputProps()} />
          {children ? (
            children
          ) : (
            <>
              {state === 'waiting' && <Empty />}
              {state === 'uploading' && <Loading />}
              {state === 'uploadError' && <Empty />}
            </>
          )}
        </div>
      </div>
    )
  }
)

interface ImageProps {
  id: number
  onDelete: () => void
  className?: string
}

const Image: FC<ImageProps> = flowMax(
  addDisplayName('Image'),
  addApi,
  addDataLoadingFromPromise('image', ({ id, api }) => api.images.get(id), [
    'id',
  ]),
  addModeContext,
  addTranslationHelpers,
  addProps(({ mode }) => ({
    classes: useStyles({ mode }),
  })),
  addWrapper((render, { classes, className, mode }) => (
    <div
      className={cx(
        classes.container,
        getModeMaskClassName(classes, mode),
        className
      )}
    >
      {render()}
    </div>
  )),
  addTranslationHelpers,
  branchIfPassesPredicate('image', isLoading, {
    returns: () => <Loading />,
  }),
  branchIfPassesPredicate('image', isError),
  addProps(({ image }) => ({
    state: image.data.state,
  })),
  addEffect(
    ({ state, reloadImage }) =>
      () => {
        if (!isImageStateProcessing(state)) {
          return
        }

        const timer = setInterval(() => {
          reloadImage()
        }, 1000)

        return () => {
          clearInterval(timer)
        }
      },
    ['state']
  ),
  branchIfPassesPredicate('state', isImageStateProcessing, {
    returns: () => <Loading />,
  }),
  addToastContext,
  addEffect(
    ({ state, onDelete, showErrorToast, t }) =>
      () => {
        if (isImageStateError(state)) {
          showErrorToast({
            title: t('admin.contentPicker.errorProcessingImage'),
            details: t('admin.general.genericError'),
          })
          onDelete()
        }
      },
    ['state']
  ),
  branchIfPassesPredicate('state', isImageStateError),
  ({ state, classes }) => (
    <>
      <img src={state.value.full} className={classes.image} alt="" />
      <div className={classes.overlay}>
        <Icon name="camera" />
      </div>
    </>
  )
)

interface VideoProps {
  id: number
  onDelete: () => void
}

const Video: FC<VideoProps> = flowMax(
  addDisplayName('Video'),
  addApi,
  addDataLoadingFromPromise('video', ({ id, api }) => api.videos.get(id), [
    'id',
  ]),
  addModeContext,
  addTranslationHelpers,
  addProps(({ mode }) => ({
    classes: useStyles({ mode }),
  })),
  addWrapper((render, { classes }) => (
    <div className={classes.container}>{render()}</div>
  )),
  addTranslationHelpers,
  branchIfPassesPredicate('video', isLoading, {
    returns: () => <Loading />,
  }),
  branchIfPassesPredicate('video', isError),
  addProps(({ video }) => ({
    state: video.data.state,
  })),
  addEffect(
    ({ state, reloadVideo }) =>
      () => {
        if (!isVideoStateProcessing(state)) {
          return
        }

        const timer = setInterval(() => {
          reloadVideo()
        }, 1000)

        return () => {
          clearInterval(timer)
        }
      },
    ['state']
  ),
  branchIfPassesPredicate('state', isVideoStateProcessing, {
    returns: () => <Loading />,
  }),
  addToastContext,
  addEffect(
    ({ state, onDelete, showErrorToast, t }) =>
      () => {
        if (isVideoStateError(state)) {
          showErrorToast({
            title: t('admin.contentPicker.errorProcessingVideo'),
            details: t('admin.general.genericError'),
          })
          onDelete()
        }
      },
    ['state']
  ),
  branchIfPassesPredicate('state', isVideoStateError),
  addProps(({ state }) => ({
    videoUrl: `https://stream.mux.com/${state.value}.m3u8`,
    thumbnailUrl: `https://image.mux.com/${state.value}/thumbnail.jpg`,
  })),
  addRef('ref', typedAs<HTMLVideoElement | null>(null)),
  addEffect(
    ({ videoUrl, ref }) =>
      () => {
        let hls: Hls | null = null

        if (!ref.current) {
          return
        }

        const video = ref.current

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = videoUrl
        } else if (Hls.isSupported()) {
          hls = new Hls()
          hls.loadSource(videoUrl)
          hls.attachMedia(video)
        } else {
          console.error("This is a legacy browser that doesn't support MSE")
        }

        return () => {
          hls?.destroy()
          video.onplaying = null
        }
      },
    ['videoUrl', 'ref', 'setVideoElementPlaying']
  ),
  ({ thumbnailUrl, ref, classes }) => (
    <>
      <video poster={thumbnailUrl} ref={ref} className={classes.image} />
      <div className={classes.overlay}>
        <Icon name="camera" />
      </div>
    </>
  )
)

interface ContentPickerProps {
  value: MediaContent | null
  onChange: (content: MediaContent | null) => void
}

export const ContentPicker: FC<ContentPickerProps> = flowMax(
  addDisplayName('ContentPicker'),
  addProps({
    mode: typedAs<Mode>('media'),
  }),
  addModeContextProvider,
  addPropTrackingRef('onChange', 'onChangeRef'),
  addHandlers({
    onUploadedImage:
      ({ onChangeRef }) =>
      (image: ApiImage) => {
        onChangeRef.current({ type: 'image', value: image.id })
      },
    onUploadedVideo:
      ({ onChangeRef }) =>
      (video: ApiVideo) => {
        onChangeRef.current({ type: 'video', value: video.id })
      },
    onDelete:
      ({ onChangeRef }) =>
      () => {
        onChangeRef.current(null)
      },
  }),
  ({ value, onUploadedImage, onUploadedVideo, onDelete }) => (
    <Uploader
      onUploadedImage={onUploadedImage}
      onUploadedVideo={onUploadedVideo}
      onDelete={onDelete}
    >
      {value &&
        (value.type === 'image' ? (
          <Image id={value.value} onDelete={onDelete} />
        ) : (
          <Video id={value.value} onDelete={onDelete} />
        ))}
    </Uploader>
  )
)

interface ImagePickerProps {
  mode: Mode
  value: number | null
  onChange: (content: number | null) => void
  className?: string
}

export const ImagePicker: FC<ImagePickerProps> = flowMax(
  addDisplayName('ImagePicker'),
  addModeContextProvider,
  addPropTrackingRef('onChange', 'onChangeRef'),
  addHandlers({
    onUploadedImage:
      ({ onChangeRef }) =>
      (image: ApiImage) => {
        onChangeRef.current(image.id)
      },
    onDelete:
      ({ onChangeRef }) =>
      () => {
        onChangeRef.current(null)
      },
  }),
  ({ value, onUploadedImage, onDelete, className }) => (
    <Uploader
      onUploadedImage={onUploadedImage}
      onDelete={onDelete}
      className={className}
    >
      {value && <Image id={value} onDelete={onDelete} className={className} />}
    </Uploader>
  )
)
